import { useState, type ReactNode, type Ref } from 'react';
import { Link } from 'react-router';
import { LogOut, Search, SlidersHorizontal, UserRound } from 'lucide-react';
import type { Principal } from '@contracts/common';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { firstLastInitials as initialsOf } from '@/lib/initials';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { OPEN_COMMAND_PALETTE_EVENT } from './command-palette';
import { SidebarToggle } from './sidebar';
import { ThemeDrawer } from './ThemeDrawer';

interface HeaderProps {
  principal: Principal;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  /** The toggle, so the shell can hand focus back to it when the drawer closes. */
  toggleRef?: Ref<HTMLButtonElement>;
  onSignOut?: () => void;
  notifications?: ReactNode;
  /** Hana's button (D109), between search and the bell. */
  assistant?: ReactNode;
  /** Where the current screen's title lands (`InTitleSlot`). */
  titleSlotRef?: Ref<HTMLDivElement>;
}

/**
 * The HyperTek prototype's top app bar (h-prototype `components/layout/header.tsx`), class for class: a
 * sticky glass bar with, on a phone only, the drawer toggle (a desktop collapses the sidebar from its foot), then
 * the current screen's title (the slot `InTitleSlot` renders into,
 * where the prototype has its search box), and on the right the search icon that opens the ⌘K palette, the
 * notifications bell, the theme button (lg and up; below lg it is in the user menu) and the user menu.
 *
 * What this product puts into that frame:
 *   - No "create" action. Work is assigned on the Workplace, through its "Việc mới" (the one primary action
 *     there), so the band holds navigation and the person's own controls only.
 *   - The bell is the real inbox (NotificationMenu), passed in so the frame still issues no request.
 *   - The user menu's trigger is named "Tài khoản của {name}"; the menu holds "Tài khoản" (the account page)
 *     and "Đăng xuất". The line under the name is the person's standing: "Quản trị viên" or "Thành viên BOD".
 *
 * The theme drawer is rendered beside the header, never inside it: `backdrop-blur` makes the header the
 * containing block of every fixed descendant, which would pin a full-height sheet to a 52 px bar.
 */
export function Header({
  principal, sidebarOpen, onToggleSidebar, toggleRef, onSignOut, notifications, assistant, titleSlotRef,
}: HeaderProps) {
  const [themeOpen, setThemeOpen] = useState(false);
  const role = principal.isAdmin ? 'Quản trị viên' : 'Thành viên BOD';

  return (
    <>
      <header data-app-banner="" className="sticky top-0 z-30 flex h-13 items-center gap-1 border-b border-[hsl(var(--h-glass-border))] bg-[hsl(var(--h-glass-bg))] px-2 backdrop-blur-md lg:gap-3 lg:px-6">
        <SidebarToggle ref={toggleRef} onClick={onToggleSidebar} isOpen={sidebarOpen} />

        {/* The current screen's title and its back control (`InTitleSlot`). */}
        <div ref={titleSlotRef} data-page-title="" className="flex min-w-0 flex-1 items-center gap-2" />

        {/* The buttons never shrink: the title fits the room that is left — two lines below `sm`, one cut line from
            `sm` up (P2 review F15, D41: `BANNER_TITLE`). */}
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {/* Search — opens the ⌘K palette */}
          <button
            type="button"
            aria-label="Tìm kiếm"
            title="Tìm kiếm (⌘K)"
            aria-haspopup="dialog"
            aria-keyshortcuts="Meta+K Control+K"
            onClick={() => window.dispatchEvent(new CustomEvent(OPEN_COMMAND_PALETTE_EVENT))}
            className="flex size-9 items-center justify-center rounded-lg transition-colors hover:bg-[hsl(var(--h-muted))]"
          >
            <Search className="h-5 w-5 text-[hsl(var(--h-card-foreground))]" aria-hidden="true" />
          </button>

          {assistant}

          {notifications}

          {/* Theme switcher — at lg and up; below lg it is an item of the user menu, so a phone band holds five. */}
          <button
            type="button"
            onClick={() => setThemeOpen(true)}
            aria-label="Màu giao diện"
            aria-haspopup="dialog"
            className="hidden size-9 items-center justify-center rounded-lg lg:flex transition-colors hover:bg-[hsl(var(--h-muted))]"
          >
            <SlidersHorizontal className="h-5 w-5 text-[hsl(var(--h-card-foreground))]" aria-hidden="true" />
          </button>

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Tài khoản của ${principal.name}`}
                className="ml-1 flex h-9 items-center gap-2 rounded-lg py-0.5 pl-0.5 pr-2 transition-colors hover:bg-[hsl(var(--h-muted))]"
              >
                <Avatar className="size-8" aria-hidden="true">
                  <AvatarFallback className="text-xs">{initialsOf(principal.name)}</AvatarFallback>
                </Avatar>
                <div className="hidden flex-col items-start leading-tight md:flex">
                  <span className="max-w-[20ch] truncate text-sm font-semibold text-[hsl(var(--h-card-foreground))]">
                    {principal.name}
                  </span>
                  <span className="text-xs text-[hsl(var(--h-muted-foreground))]">{role}</span>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex flex-col leading-tight">
                <span className="truncate">{principal.name}</span>
                <span className="text-xs font-normal text-[hsl(var(--h-muted-foreground))]">{role}</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/account">
                  <UserRound className="mr-2 h-4 w-4" aria-hidden="true" /> Tài khoản
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setThemeOpen(true)} className="lg:hidden">
                <SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden="true" /> Màu giao diện
              </DropdownMenuItem>
              {onSignOut && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={onSignOut}
                    className="text-[hsl(var(--h-error))] focus:text-[hsl(var(--h-error))]"
                  >
                    <LogOut className="mr-2 h-4 w-4" aria-hidden="true" /> Đăng xuất
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {themeOpen && <ThemeDrawer onClose={() => setThemeOpen(false)} />}
    </>
  );
}
